import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import { toFullName } from '@repo/toolkit/to-full-name'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import Typography from '@repo/ui/typography'
import { TFunction } from 'i18next'
import { z, ZodType } from 'zod'

import { eventStatusEnumMapping } from '../../../lib/types/enums/center-event/event-status.enum'
import { EventTypeEnum } from '../../../lib/types/enums/center-event/event-type.enum'
import { EventActionTypeEnum } from '../../../lib/types/enums/center-event-action/event-action-type.enum'
import { CenterProperties } from '../../../lib/types/models/center/properties.center'
import { CenterEvent } from '../../../lib/types/models/center-event/center-event'
import { CenterEventInput } from '../../../lib/types/models/center-event/center-event-input'
import { CenterEventProperties } from '../../../lib/types/models/center-event/properties.center-event'
import { CenterEventActionInput } from '../../../lib/types/models/center-event-action/center-event-action-input'
import {
  createEnumArraySchema,
  createEnumSchema
} from '../../../lib/utils/validations/enum-validation'
import { FILE_VALIDATION } from '../../../lib/utils/validations/file.validation'
import {
  createNumberArraySchema,
  createNumberSchema
} from '../../../lib/utils/validations/int-array-validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { LocaleKeys } from '../../../locale/translation-types'
import { getOlName } from '../../../views/ols/center/utils/functions'
import CountryFlag from '../../elements/country-flag'
import CenterPeriodCell from './center-period-cell'

export const CENTER_EVENT_SCHEMA: ZodType<CenterEventInput> = z.object({
  reporter: createStringSchema("Personne qui fait part de l'évènement"),
  types: createEnumArraySchema({ enumType: EventTypeEnum, fieldName: "Type d'évènement" }),
  description: createStringSchema('Description'),
  assignedToId: createNumberSchema("Assigner l'évènement", { nullish: true }),
  files: FILE_VALIDATION().array().default([]),
  enrollmentIds: createNumberArraySchema('Inscriptions').optional()
})

export const CENTER_EVENT_ACTION_SCHEMA: ZodType<CenterEventActionInput> = z.object({
  actionType: createEnumSchema({ enumType: EventActionTypeEnum, fieldName: 'Source' }),
  name: createStringSchema('NOM'),
  description: createStringSchema('Description'),
  files: FILE_VALIDATION().array().default([])
})

export type CenterEventVersion = 'FULL' | 'PARTIAL' | 'VIEW' | 'OL_VIEW'

type Translate = TFunction<AvailableLanguages, undefined>

interface CenterEventColumnConfig<V extends readonly CenterEventVersion[]> {
  accessorKey: string
  versions: V
  headers: { [K in V[number]]: string } & { [K in Exclude<CenterEventVersion, V[number]>]?: never }
  order?: { [K in V[number]]: number } & { [K in Exclude<CenterEventVersion, V[number]>]?: never }
  enableSorting?: boolean
  cell: (params: { row: { original: CenterEvent } }, t: Translate) => JSX.Element | string
}

function createCenterEventColumnConfig<V extends readonly CenterEventVersion[]>(
  config: CenterEventColumnConfig<V>
): CenterEventColumnConfig<V> {
  return config
}
const centerEventColumnConfigs = [
  createCenterEventColumnConfig({
    accessorKey: 'types',
    versions: ['FULL', 'PARTIAL', 'VIEW', 'OL_VIEW'],
    headers: {
      FULL: "Type d'évènement",
      PARTIAL: 'Type',
      VIEW: "Type d'évènement",
      OL_VIEW: 'center.events.columns.type'
    },
    order: { FULL: 1, PARTIAL: 1, VIEW: 1, OL_VIEW: 1 },
    cell: ({ row }, t) => (
      <div className="flex flex-row items-center flex-wrap text-wrap gap-2">
        {row.original.types.map((type) => t(`center.event.type.enum.${type}`)).join(', ')}
      </div>
    )
  }),
  createCenterEventColumnConfig({
    accessorKey: 'createdAt',
    versions: ['FULL', 'PARTIAL', 'VIEW', 'OL_VIEW'],
    headers: {
      FULL: 'Evènement créé le',
      PARTIAL: 'Remonté le',
      VIEW: 'Evènement créé le',
      OL_VIEW: 'center.events.columns.created.at'
    },
    order: { FULL: 6, PARTIAL: 2, VIEW: 4, OL_VIEW: 4 },
    enableSorting: true,
    cell: ({ row }) => <Badge text={DateFormatter.ddMMYYYY_HHMM(row.original.createdAt)} />
  }),
  createCenterEventColumnConfig({
    accessorKey: 'center.name',
    versions: ['FULL'],
    headers: { FULL: 'Centre' },
    order: { FULL: 3 },
    enableSorting: true,
    cell: ({ row }) => row.original.center?.name
  }),
  createCenterEventColumnConfig({
    accessorKey: 'centerToJourneys',
    versions: ['FULL', 'VIEW', 'OL_VIEW'],
    headers: {
      FULL: 'Dates de séjour ',
      VIEW: 'Dates de séjour',
      OL_VIEW: 'center.events.columns.dates'
    },
    order: { FULL: 4, VIEW: 2, OL_VIEW: 3 },
    cell: ({ row }) => {
      const dates = CenterProperties.getCenterDates(row.original.center)
      if (!dates) return <>-</>
      return <Badge text={dates} />
    }
  }),
  createCenterEventColumnConfig({
    accessorKey: 'student.childLastName',
    versions: ['FULL', 'PARTIAL', 'OL_VIEW'],
    headers: {
      FULL: 'NOM Prénom du jeune',
      PARTIAL: 'Jeune concerné',
      OL_VIEW: 'center.events.columns.student'
    },
    order: { FULL: 2, PARTIAL: 3, OL_VIEW: 2 },
    enableSorting: true,
    cell: ({ row }) => {
      const student = row.original.student
      if (!student) return <>-</>
      return (
        <Typography variant="label-medium">
          {toFullName(student.childFirstName, student.childLastName, {
            options: { mode: 'lastname-firstname' }
          })}
        </Typography>
      )
    }
  }),
  createCenterEventColumnConfig({
    accessorKey: 'assignedTo.firstName',
    versions: ['FULL', 'PARTIAL', 'VIEW', 'OL_VIEW'],
    headers: {
      FULL: 'Suivi par',
      PARTIAL: 'Traité par',
      VIEW: 'Suivi par',
      OL_VIEW: 'center.events.columns.assigned.to'
    },
    order: { FULL: 5, PARTIAL: 4, VIEW: 3, OL_VIEW: 7 },
    enableSorting: true,
    cell: ({ row }) => (
      <Typography variant="label-medium">
        {valueRender(row.original.assignedTo, (user) =>
          toFullName(user.firstName, user.lastName)
        ) || '-'}
      </Typography>
    )
  }),
  createCenterEventColumnConfig({
    accessorKey: 'actions.createdAt',
    versions: ['FULL', 'VIEW', 'OL_VIEW'],
    headers: {
      FULL: 'Dernière activité',
      VIEW: 'Dernière activité',
      OL_VIEW: 'center.events.columns.last.activity'
    },
    order: { FULL: 7, VIEW: 5, OL_VIEW: 5 },
    cell: ({ row }) =>
      row.original.lastActivityAt ? (
        <Badge text={DateFormatter.ddMMYYYY_HHMM(row.original.lastActivityAt)} />
      ) : (
        '-'
      )
  }),
  createCenterEventColumnConfig({
    accessorKey: 'inactiveDuration',
    versions: ['FULL', 'OL_VIEW'],
    headers: { FULL: "Durée d'inactivité", OL_VIEW: 'center.events.columns.inactive.duration' },
    order: { FULL: 8, OL_VIEW: 6 },
    enableSorting: true,
    cell: ({ row }, t) =>
      row.original.lastActivityAt ? (
        <Badge
          text={CenterEventProperties.getInactivityDuration(row.original.lastActivityAt, (total) =>
            t('center.events.inactive.duration.days', { total })
          )}
        />
      ) : (
        '-'
      )
  }),
  createCenterEventColumnConfig({
    accessorKey: 'center.journeyPlace.country.name',
    versions: ['OL_VIEW', 'FULL'],
    headers: { OL_VIEW: 'center.events.columns.country', FULL: 'Pays' },
    order: { OL_VIEW: 8, FULL: 10 },
    cell: ({ row }) => (
      <CountryFlag possibleCountry={row.original.center.journeyPlace?.country.name} />
    )
  }),
  createCenterEventColumnConfig({
    accessorKey: 'center.centerToJourneys[0].date',
    versions: ['OL_VIEW', 'FULL'],
    headers: { OL_VIEW: 'center.events.columns.period', FULL: 'Période' },
    order: { OL_VIEW: 9, FULL: 11 },
    cell: ({ row }) => <CenterPeriodCell centerEvent={row.original} />
  }),
  createCenterEventColumnConfig({
    accessorKey: 'center.ol.mainContactFirstName',
    versions: ['FULL'],
    headers: { FULL: 'Prestataire' },
    order: { FULL: 12 },
    cell: ({ row }) => (
      <Typography variant="label-medium">
        {getOlName(row.original.center.ol, { lastNameFirst: true })}
      </Typography>
    )
  }),
  createCenterEventColumnConfig({
    accessorKey: 'status',
    versions: ['FULL', 'PARTIAL', 'VIEW', 'OL_VIEW'],
    headers: {
      FULL: 'Statut',
      PARTIAL: 'Statut',
      VIEW: 'Statut',
      OL_VIEW: 'center.events.columns.status'
    },
    order: { FULL: 9, PARTIAL: 5, VIEW: 6, OL_VIEW: 10 },
    enableSorting: true,
    cell: ({ row }, t) => (
      <Badge
        variant={eventStatusEnumMapping[row.original.status].variant}
        text={t(`center.event.status.enum.${row.original.status}`)}
      />
    )
  })
]

// OL_VIEW headers are locale keys: the OL space is translated, the admin versions stay in French
export const centerEventsColumns = (
  version: CenterEventVersion,
  t: Translate
): Column<CenterEvent>[] =>
  centerEventColumnConfigs.reduce<Column<CenterEvent>[]>((acc, config) => {
    const header = config.headers[version]
    if (header) {
      acc.push({
        accessorKey: config.accessorKey,
        header: version === 'OL_VIEW' ? t(header as LocaleKeys) : header,
        enableSorting: config.enableSorting,
        cell: (params) => config.cell(params, t)
      })
    }
    return acc
  }, [])

/**
 * Returns the center event columns for a given version.
 * It picks the header text and order based on the provided version,
 * then sorts the columns accordingly.
 */
export const getCenterEventColumns = (
  version: CenterEventVersion,
  t: Translate
): Column<CenterEvent>[] => {
  return centerEventsColumns(version, t)
    .map((column) => {
      // Find the matching configuration to pick the correct header and order.
      const config = centerEventColumnConfigs.find((c) => c.accessorKey === column.accessorKey)
      if (!config || config.headers[version] === undefined) {
        return null
      }
      return {
        column,
        order: config.order ? config.order[version] : 0
      }
    })
    .filter((item): item is { column: Column<CenterEvent>; order: number } => item !== null)
    .sort((a, b) => a.order - b.order)
    .map(({ column }) => column)
}
